import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { MaturityItem } from '../../contracts'
import { formatMoney } from '../../formatters'

interface MaturityTimelineProps {
  items: MaturityItem[]
  height?: number
}

function formatDateLabel(value: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return value
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

export function MaturityTimeline({ items, height = 220 }: MaturityTimelineProps) {
  if (items.length === 0) return null
  const data = items.map((item) => ({
    dateLabel: formatDateLabel(item.date),
    amount: item.amount,
    label: item.label,
    rawDate: item.date,
  }))

  return (
    <div className="chart-block chart-block--timeline">
      <h4>Upcoming maturities</h4>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={(value: number) => formatMoney(value)}
              tick={{ fontSize: 12 }}
              width={80}
            />
            <Tooltip
              formatter={(value: number) => [formatMoney(value), 'Maturity']}
              labelFormatter={(label: string, payload) => {
                const first = Array.isArray(payload) ? payload[0] : null
                const item =
                  first && typeof first === 'object'
                    ? (first as { payload?: { label?: string } }).payload
                    : null
                return item?.label ? `${label} · ${item.label}` : label
              }}
              contentStyle={{
                background: 'var(--surface, #fff)',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
            <Bar dataKey="amount" fill="#5b8def" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="chart-block__legend chart-block__legend--inline">
        {items.map((item) => (
          <li key={`${item.date}:${item.label}`}>
            <strong>{formatDateLabel(item.date)}</strong>
            <small className="muted">
              {item.label} · {formatMoney(item.amount)}
            </small>
          </li>
        ))}
      </ul>
    </div>
  )
}
